import { useState, useRef } from 'react';

import Script from 'next/script';
import { Box, Dialog, DialogContent, SwipeableDrawer, useMediaQuery, useTheme } from '@mui/material';

import { useAppContext } from '@/context';
import { Maybe, Scalars } from '@/types/generated';

import { CloseButton } from '../TAGDialog/TAGDialog';
import { DynamicFormConfig, DynamicFormSchema as TDynamicFormSchema } from '../DynamicForm/types/DynamicFormTypes';
import ZenotiWrapper from '../DynamicForm/ZenotiWrapper';

import {
	getDialogWrapperStyles,
	getDrawerWrapperStyles,
	quizInPageWrapperStyles,
	screenReaderOnlyStyles,
} from './Quiz.styles';
import { IStateObject, quizGA4DataLayerPush, TFormData } from './Quiz.helpers';
import Questions from './Questions/Questions';
import UserDetailsForm from './UserDetailsForm/UserDetailsForm';

// Extend the Window interface to include quizAutoAdvanceEnabled and quizFormDataDebug
declare global {
	interface Window {
		quizAutoAdvanceEnabled?: boolean;
		quizFormDataDebug?: Maybe<Scalars['JSON']>;
	}
}

export interface IQuiz {
	/**
	 * Quiz Type for different business logic
	 */
	quizType: string;
	/**
	 * JSON Form data from contentful
	 */
	quizFormData: Maybe<Scalars['JSON']>;
	/**
	 * Quiz Submit endpoint url from contentful
	 */
	submitEndpointUrl: string;
	/**
	 * If <b>true</b>, the component is shown.
	 * @type {boolean}
	 *
	 */
	showInDialogOrDrawer?: boolean;
	/**
	 * If <b>true</b>, the component is shown.
	 * @type {boolean}
	 *
	 */
	openQuiz?: boolean;
	/**
	 * Callback to fire when the component needs to be closed.
	 */
	handleCloseQuiz?: () => void;
	/**
	 * If true, the component utilizes the FormBuilder,
	 * and completely bypasses all of Quiz
	 * @type {boolean}
	 */
	isFormBuilder?: boolean;
	/**
	 * JSON Form builder config from contentful
	 */
	formBuilderConfig?: DynamicFormConfig;
	/**
	 * redirect path for poor credit users for CC specific
	 */
	clearChoicePoorCreditRedirectPath?: string;
	/**
	 * redirect path for spanish quiz good credit users
	 */
	clearChoiceSpanishRedirectPath?: string;
}

interface QuizFromJsonContentWrapper {
	showInDialogOrDrawer: boolean;
	handleClose: () => void;
	formType: string;
	state: IStateObject;
	updateState: (newState: Partial<IStateObject>) => void;
	submitEndpointUrl: string;
	quizType: string;
	clearChoicePoorCreditRedirectPath: string;
	clearChoiceSpanishRedirectPath: string;
	isAutoAdvanceEnabled: boolean;
}
interface QuizFromFormBuilderContentWrapper {
	showInDialogOrDrawer: boolean;
	handleClose: () => void;
	quizFormData: TFormData;
	formBuilderConfig: DynamicFormConfig;
}

const QuizFromJsonContentWrapper = (props: QuizFromJsonContentWrapper) => {
	const {
		showInDialogOrDrawer = false,
		handleClose,
		formType,
		state,
		updateState,
		submitEndpointUrl,
		quizType,
		clearChoicePoorCreditRedirectPath,
		clearChoiceSpanishRedirectPath,
		isAutoAdvanceEnabled,
	} = props;

	return (
		<>
			{showInDialogOrDrawer && <CloseButton onClose={handleClose} />}

			{/* WCAG Compliance: Hidden announcement for auto-advance behavior */}
			{formType === 'Questions' && isAutoAdvanceEnabled && (
				<output aria-live="polite" aria-atomic="true" style={screenReaderOnlyStyles as React.CSSProperties}>
					This quiz will automatically advance to the next question after you make a selection. You can
					navigate using your keyboard or assistive technology as normal.
				</output>
			)}

			{/* Render Questions initially  */}
			{formType === 'Questions' && (
				<Questions
					state={state}
					setState={updateState}
					showInDialogOrDrawer={showInDialogOrDrawer}
					isAutoAdvanceEnabled={isAutoAdvanceEnabled}
					quizType={quizType}
				/>
			)}
			{/* Render user details form */}
			{formType === 'UserDetails' && (
				<UserDetailsForm
					state={state}
					setState={updateState}
					submitEndpointUrl={submitEndpointUrl}
					showInDialogOrDrawer={showInDialogOrDrawer}
					quizType={quizType}
					clearChoicePoorCreditRedirectPath={clearChoicePoorCreditRedirectPath}
					clearChoiceSpanishRedirectPath={clearChoiceSpanishRedirectPath}
				/>
			)}
		</>
	);
};

const QuizFromFormBuilderContentWrapper = (props: QuizFromFormBuilderContentWrapper) => {
	const { showInDialogOrDrawer = false, handleClose, quizFormData, formBuilderConfig } = props;

	return (
		<>
			{showInDialogOrDrawer && <CloseButton onClose={handleClose} />}
			<ZenotiWrapper
				defaultFormData={{
					minAge: 18,
					minBMI: 27,
					todaysDate: new Date().toLocaleDateString('en-CA'),
					contactConsent: true,
				}}
				defaultSchema={quizFormData as unknown as TDynamicFormSchema}
				schemaJSON={JSON.stringify(quizFormData)}
				config={formBuilderConfig}
			/>
		</>
	);
};

export default function Quiz({
	quizType,
	quizFormData,
	submitEndpointUrl,
	openQuiz,
	handleCloseQuiz,
	showInDialogOrDrawer = false,
	isFormBuilder = false,
	formBuilderConfig = {},
	clearChoicePoorCreditRedirectPath = '',
	clearChoiceSpanishRedirectPath = '',
}: Readonly<IQuiz>) {
	const staticForm: 'Questions' | 'UserDetails' =
		(quizFormData as TFormData)?.form?.form_questions?.length > 0 ? 'Questions' : 'UserDetails';

	const [state, setState] = useState<IStateObject>({
		activeQuestionIndex: 0,
		formData: quizFormData as TFormData,
		formType: isFormBuilder ? 'DynamicForm' : staticForm,
		fieldHasError: false,
	});

	const theme = useTheme();
	const { config, environment, stateCodeFromHeaders } = useAppContext();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const autoAdvanceRef = useRef<HTMLDivElement>(null);

	// Safe calculation of isAutoAdvanceEnabled
	const isAutoAdvanceEnabled = (() => {
		if (!quizFormData) {
			return false;
		}

		let parsedData: TFormData | null = null;

		if (typeof quizFormData === 'string') {
			try {
				parsedData = JSON.parse(quizFormData) as TFormData;
			} catch (error) {
				console.warn('Failed to parse quizFormData JSON:', error);
				return false;
			}
		} else {
			parsedData = quizFormData as TFormData;
		}

		return parsedData?.form?.isAutoAdvanceEnabled ?? false;
	})();

	const { formType, formData } = state;

	// NOTE: form_step 'start' is emitted from Questions.tsx when the user answers
	// the FIRST question — not here on quiz load/open. DCP-14686 corrects DCP-14512,
	// which fired 'start' on visibility (the quiz merely appearing), overcounting
	// funnel entries for users who never engaged.

	const updateState = (newState: Partial<IStateObject>) => {
		setState({
			...state,
			...newState,
		});
	};

	const handleClose = () => {
		updateState({
			formData: quizFormData as TFormData,
			formType: isFormBuilder ? 'DynamicForm' : staticForm,
			activeQuestionIndex: 0,
		});

		if (!isFormBuilder) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
			quizGA4DataLayerPush(config.name, 'cancel', 'N/A', formData as TFormData, stateCodeFromHeaders);
		}
		if (handleCloseQuiz) {
			handleCloseQuiz();
		}
	};

	if ((showInDialogOrDrawer && !openQuiz) || !formData) {
		return null;
	}

	if (isFormBuilder) {
		// form builder quiz in a dialog
		if (showInDialogOrDrawer) {
			return (
				<>
					{isSmallScreen ? (
						<SwipeableDrawer
							open
							onClose={() => null}
							onOpen={() => null}
							anchor="bottom"
							sx={getDrawerWrapperStyles(true)}
							data-testid={`${formType}_drawer`}
						>
							<QuizFromFormBuilderContentWrapper
								showInDialogOrDrawer
								handleClose={handleClose}
								quizFormData={quizFormData as TFormData}
								formBuilderConfig={formBuilderConfig}
							/>
						</SwipeableDrawer>
					) : (
						<Dialog
							open
							sx={getDialogWrapperStyles}
							fullWidth
							maxWidth="lg"
							data-testid={`${formType}_dialog`}
						>
							<DialogContent>
								<QuizFromFormBuilderContentWrapper
									showInDialogOrDrawer
									handleClose={handleClose}
									quizFormData={quizFormData as TFormData}
									formBuilderConfig={formBuilderConfig}
								/>
							</DialogContent>
						</Dialog>
					)}
				</>
			);
		}

		// form builder quiz in a page
		return (
			<Box data-testid="quiz_in_page">
				<Script
					src={
						environment === 'prod'
							? config.services.prod.CONFIG_SCRIPT_URL
							: config.services.nonprod.CONFIG_SCRIPT_URL
					}
					strategy="lazyOnload"
				/>
				<Script
					src={
						environment === 'prod'
							? config.services.prod.LOADER_SCRIPT_URL
							: config.services.nonprod.LOADER_SCRIPT_URL
					}
					strategy="lazyOnload"
				/>
				<QuizFromFormBuilderContentWrapper
					showInDialogOrDrawer={false}
					handleClose={() => null}
					quizFormData={quizFormData as TFormData}
					formBuilderConfig={formBuilderConfig}
				/>
			</Box>
		);
	}

	if (showInDialogOrDrawer) {
		// quiz from json in a dialog
		return (
			<>
				{isSmallScreen ? (
					<SwipeableDrawer
						open
						onClose={() => null}
						onOpen={() => null}
						anchor="bottom"
						sx={getDrawerWrapperStyles(false)}
						data-testid={`${formType}_drawer`}
					>
						<QuizFromJsonContentWrapper
							showInDialogOrDrawer
							handleClose={handleClose}
							formType={formType}
							state={state}
							updateState={updateState}
							submitEndpointUrl={submitEndpointUrl}
							quizType={quizType}
							clearChoicePoorCreditRedirectPath={clearChoicePoorCreditRedirectPath}
							clearChoiceSpanishRedirectPath={clearChoiceSpanishRedirectPath}
							isAutoAdvanceEnabled={isAutoAdvanceEnabled}
						/>
					</SwipeableDrawer>
				) : (
					<Dialog open sx={getDialogWrapperStyles} fullWidth maxWidth="lg" data-testid={`${formType}_dialog`}>
						<DialogContent>
							<div ref={autoAdvanceRef}>
								<QuizFromJsonContentWrapper
									showInDialogOrDrawer
									handleClose={handleClose}
									formType={formType}
									state={state}
									updateState={updateState}
									submitEndpointUrl={submitEndpointUrl}
									quizType={quizType}
									clearChoicePoorCreditRedirectPath={clearChoicePoorCreditRedirectPath}
									clearChoiceSpanishRedirectPath={clearChoiceSpanishRedirectPath}
									isAutoAdvanceEnabled={isAutoAdvanceEnabled}
								/>
							</div>
						</DialogContent>
					</Dialog>
				)}
			</>
		);
	}

	// quiz from json in a page
	const getBoxStyles = () => {
		if (isAutoAdvanceEnabled) {
			return {
				minHeight: 'calc(-12.5rem + 100vh)',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
			};
		}

		if ((quizFormData as TFormData)?.form?.formHeightAuto) {
			return { minHeight: 'auto' };
		}
		return {};
	};

	return (
		<Box
			data-testid="quiz_in_page"
			sx={{
				...quizInPageWrapperStyles,
				...getBoxStyles(),
			}}
		>
			<QuizFromJsonContentWrapper
				showInDialogOrDrawer={false}
				handleClose={handleClose}
				formType={formType}
				state={state}
				updateState={updateState}
				submitEndpointUrl={submitEndpointUrl}
				quizType={quizType}
				clearChoicePoorCreditRedirectPath={clearChoicePoorCreditRedirectPath}
				clearChoiceSpanishRedirectPath={clearChoiceSpanishRedirectPath}
				isAutoAdvanceEnabled={isAutoAdvanceEnabled}
			/>
		</Box>
	);
}
